import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { sendCustomerOtp, verifyCustomerOtp } from '../../services/api';
import { toast } from '../../lib/toast';

const RESEND_COOLDOWN_SECONDS = 30;
// Loose E.164: optional +, digits only after normalising spaces/dashes/parens.
const PHONE_RE = /^\+?[1-9]\d{7,14}$/;

// Curated country list — Oman first (default) + the rest of the GCC + common
// adjacent markets. Add more here when the product expands. Each entry has a
// dial code with leading +, an emoji flag, and a name (English; the Select
// uses dial code + flag for the visible label so it works across all locales).
const COUNTRIES = [
  { code: 'OM', dial: '+968', flag: '🇴🇲', name: 'Oman' },
  { code: 'SA', dial: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: 'AE', dial: '+971', flag: '🇦🇪', name: 'United Arab Emirates' },
  { code: 'QA', dial: '+974', flag: '🇶🇦', name: 'Qatar' },
  { code: 'BH', dial: '+973', flag: '🇧🇭', name: 'Bahrain' },
  { code: 'KW', dial: '+965', flag: '🇰🇼', name: 'Kuwait' },
  { code: 'EG', dial: '+20', flag: '🇪🇬', name: 'Egypt' },
  { code: 'JO', dial: '+962', flag: '🇯🇴', name: 'Jordan' },
  { code: 'US', dial: '+1', flag: '🇺🇸', name: 'United States' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'ES', dial: '+34', flag: '🇪🇸', name: 'Spain' },
];
const DEFAULT_DIAL = '+968';

function digitsOnly(raw) {
  return (raw || '').replace(/\D/g, '');
}

export default function OtpCheckoutDialog({ open, restaurantId, onClose, onVerified }) {
  const { t } = useTranslation();
  const [step, setStep] = useState('phone'); // 'phone' | 'code'
  const [dialCode, setDialCode] = useState(DEFAULT_DIAL);
  const [phone, setPhone] = useState('');       // local digits only, no country code
  const [fullPhone, setFullPhone] = useState(''); // dial + local, captured at /send time
  const [code, setCode] = useState('');
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const [error, setError] = useState('');
  const codeInputRef = useRef(null);

  // Reset state every time the dialog opens, so a previous attempt's code
  // does not bleed into the next one. Country code resets to Oman.
  useEffect(() => {
    if (open) {
      setStep('phone');
      setDialCode(DEFAULT_DIAL);
      setPhone('');
      setFullPhone('');
      setCode('');
      setError('');
      setResendIn(0);
    }
  }, [open]);

  // Resend countdown.
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(id);
  }, [resendIn]);

  // Autofocus the code input when we advance.
  useEffect(() => {
    if (step === 'code' && codeInputRef.current) {
      codeInputRef.current.focus();
    }
  }, [step]);

  const handleSend = async () => {
    setError('');
    const local = digitsOnly(phone);
    const combined = `${dialCode}${local}`;
    if (!PHONE_RE.test(combined)) {
      setError(t('menu.checkout.otp.invalidPhone'));
      return;
    }
    setSending(true);
    try {
      await sendCustomerOtp(restaurantId, combined);
      setFullPhone(combined);
      setStep('code');
      setResendIn(RESEND_COOLDOWN_SECONDS);
      toast.success(t('menu.checkout.otp.codeSent'));
    } catch (err) {
      const message = err?.response?.data?.error || t('menu.checkout.otp.sendFailed');
      setError(message);
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || sending || !fullPhone) return;
    setError('');
    setSending(true);
    try {
      await sendCustomerOtp(restaurantId, fullPhone);
      setResendIn(RESEND_COOLDOWN_SECONDS);
      toast.success(t('menu.checkout.otp.codeResent'));
    } catch (err) {
      const message = err?.response?.data?.error || t('menu.checkout.otp.sendFailed');
      setError(message);
    } finally {
      setSending(false);
    }
  };

  const handleVerify = async () => {
    setError('');
    if (!/^\d{4,8}$/.test(code.trim())) {
      setError(t('menu.checkout.otp.invalidCode'));
      return;
    }
    setVerifying(true);
    try {
      const { data } = await verifyCustomerOtp(restaurantId, fullPhone, code.trim());
      if (data?.checkout_token) {
        onVerified(data.checkout_token, fullPhone);
      } else {
        setError(t('menu.checkout.otp.invalidCode'));
      }
    } catch (err) {
      const body = err?.response?.data || {};
      const message =
        (body.tooManyAttempts && t('menu.checkout.otp.tooManyAttempts')) ||
        (body.codeExpired && t('menu.checkout.otp.codeExpired')) ||
        (body.invalidCode && t('menu.checkout.otp.invalidCode')) ||
        body.error ||
        t('menu.checkout.otp.invalidCode');
      setError(message);
    } finally {
      setVerifying(false);
    }
  };

  const isPhoneStep = step === 'phone';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth aria-labelledby="otp-dialog-title">
      <DialogTitle id="otp-dialog-title">
        {isPhoneStep
          ? t('menu.checkout.otp.phoneTitle')
          : t('menu.checkout.otp.codeTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
          {isPhoneStep
            ? t('menu.checkout.otp.phonePrompt')
            : t('menu.checkout.otp.codePrompt', { phone: fullPhone })}
        </Typography>

        {isPhoneStep ? (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'stretch', mb: 1 }}>
            <Select
              value={dialCode}
              onChange={(e) => setDialCode(e.target.value)}
              disabled={sending}
              aria-label={t('menu.checkout.otp.countryLabel')}
              size="small"
              sx={{ minWidth: 130, flexShrink: 0, '& .MuiSelect-select': { dir: 'ltr' } }}
              renderValue={(value) => {
                const c = COUNTRIES.find((x) => x.dial === value);
                return c ? `${c.flag} ${c.dial}` : value;
              }}
            >
              {COUNTRIES.map((c) => (
                <MenuItem key={c.code} value={c.dial} dir="ltr">
                  <span style={{ marginInlineEnd: 8 }}>{c.flag}</span>
                  <span style={{ marginInlineEnd: 8 }}>{c.name}</span>
                  <span style={{ color: 'var(--text-secondary, #6b7280)' }}>{c.dial}</span>
                </MenuItem>
              ))}
            </Select>
            <TextField
              label={t('menu.checkout.otp.phoneLabel')}
              placeholder={t('menu.checkout.otp.phonePlaceholder')}
              value={phone}
              onChange={(e) => setPhone(digitsOnly(e.target.value))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !sending) handleSend(); }}
              type="tel"
              autoFocus
              fullWidth
              size="small"
              inputProps={{
                dir: 'ltr',
                inputMode: 'tel',
                maxLength: 15,
                'aria-label': t('menu.checkout.otp.phoneLabel'),
              }}
              disabled={sending}
            />
          </Box>
        ) : (
          <Box>
            <TextField
              inputRef={codeInputRef}
              label={t('menu.checkout.otp.codeLabel')}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              onKeyDown={(e) => { if (e.key === 'Enter' && !verifying) handleVerify(); }}
              type="text"
              fullWidth
              inputProps={{
                dir: 'ltr',
                inputMode: 'numeric',
                maxLength: 8,
                autoComplete: 'one-time-code',
                'aria-label': t('menu.checkout.otp.codeLabel'),
              }}
              sx={{ mb: 1 }}
              disabled={verifying}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
              <Button
                size="small"
                onClick={() => { setStep('phone'); setError(''); }}
                disabled={verifying || sending}
              >
                {t('menu.checkout.otp.changePhone')}
              </Button>
              <Button
                size="small"
                onClick={handleResend}
                disabled={resendIn > 0 || sending || verifying}
              >
                {resendIn > 0
                  ? t('menu.checkout.otp.resendCooldown', { seconds: resendIn })
                  : t('menu.checkout.otp.resendCode')}
              </Button>
            </Box>
          </Box>
        )}

        {error && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={sending || verifying}>
          {t('common.cancel')}
        </Button>
        {isPhoneStep ? (
          <Button
            variant="contained"
            onClick={handleSend}
            disabled={sending}
            startIcon={sending ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          >
            {t('menu.checkout.otp.sendCode')}
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleVerify}
            disabled={verifying || code.length < 4}
            startIcon={verifying ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
          >
            {t('menu.checkout.otp.verifyAndOrder')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
